// react
import React, { useEffect } from "react";
// ui component
import { Button, Input, Select } from "../../../components/UI";
// constants (icon)
import { GetDetailsIcon } from "../../../constants/icons";
import { ReactComponent as WarehouseSvg } from "../assets/warehouse.svg";
import { ReactComponent as CourierSvg } from "../assets/courier.svg";
// services
import { getCityList } from "../../../../services/area";
import {
  listDestinationBranches,
  listSourceBranches,
} from "../../../../services/inquiry";
// custom state
import { useDomesticForm } from "./useCustomeState";
// utils (helper | validation)
import { getCityOnPincode, getOptions } from "../../../utils/helper";
import { validatePincode } from "../../../utils/validation";

const first = [<WarehouseSvg className="navigation-icon text-white-900" style={{width: "18px", height: "18px"}} />, <CourierSvg style={{width: "18px", height: "18px"}} className="navigation-icon text-black-900" />];
const second = [<WarehouseSvg style={{width: "18px", height: "18px"}} className="navigation-icon text-black-900" />, <CourierSvg style={{width: "18px", height: "18px"}} className="navigation-icon text-white-900" />];

// type
const deliveryType = [
  "Branch to Branch Delivery",
  "Door Delivery Service",
] as const;
export type ActiveDeliveryType = (typeof deliveryType)[number];

// FC: Domestic Get Estimate Component
export default function DomesticForm() {
  // State
  const {
    activeDelivery,
    setActiveDelivery,
    cities,
    setCities,
    sourceBranchList,
    setSourceBranchList,
    destinationBranchList,
    setDestinationBranchList,
    sourcePincode,
    setSourcePincode,
    destinationPincode,
    setDestinationPincode,
    sourceCity,
    setSourceCity,
    destinationCity,
    setDestinationCity,
    sourceBranch,
    setSourceBranch,
    destinationBranch,
    setDestinationBranch,
    b2bSourceCity,
    setB2bSourceCity,
    b2bDestinationCity,
    setB2bDestinationCity,
  } = useDomesticForm();

  // does: set the city options
  const cityOptions = getOptions(cities, /^[a-zA-Z]+$/);
  // does: set the source branch options
  const filteredSourceBranchListOptions = sourceBranchList
    .filter(
      (b: any) =>
        b2bSourceCity.value.length !== 24 || b.city === b2bSourceCity.value
    )
    .map((b: any) => {
      return {
        value: b._id,
        label: b.name,
      };
    });
  // does: set the destination branch options
  const filteredDestinationBranchListOptions = destinationBranchList
    .filter(
      (b: any) =>
        b2bDestinationCity.value.length !== 24 ||
        b.city === b2bDestinationCity.value
    )
    .map((b: any) => {
      return {
        value: b._id,
        label: b.name,
      };
    });
  // does: find the cities
  const setUpData = async () => {
    try {
      const [cityList, branchesList] = await Promise.all([
        getCityList(),
        listSourceBranches(),
      ]);
      setCities(cityList);
      setSourceBranchList(branchesList);
    } catch (err: any) {}
  };
  // does: fetch destination branch list according to the source branch list (B2B)
  const fetchDestinationBranchList = async () => {
    try {
      if (sourceBranch.value && sourceBranch.value.length == 24) {
        const res = await listDestinationBranches(sourceBranch.value);
        setDestinationBranchList(res);
      }
    } catch (err: any) {
      console.log(err);
    }
  };

  // useEffect
  // does: call the setUpData on the first render
  useEffect(() => {
    setUpData();
  }, []);
  // does: fetch destination branch list whenever user inputs source branch value
  useEffect(() => {
    fetchDestinationBranchList();
  }, [sourceBranch.value]);
  // does: get the source city on source pincode value enter (D2D)
  useEffect(() => {
    if (typeof sourcePincode !== "object") return;
    getCityOnPincode(sourcePincode.value, cities, setSourceCity);
  }, [typeof sourcePincode === "object" && sourcePincode.value]);
  // does: get the destination city on destination pincode value enter (D2D)
  useEffect(() => {
    if (typeof destinationPincode !== "object") return;
    getCityOnPincode(destinationPincode.value, cities, setDestinationCity);
  }, [typeof destinationPincode === "object" && destinationPincode]);

  // component return
  // Branch to Branch Delivery
  if (activeDelivery === "Branch to Branch Delivery") {
    return (
      <div className="| flex-col gap-1">
        <ul className="| two-column t-one-column m-0">
          {deliveryType.length > 0 &&
            deliveryType.map((item, index) => (
              <li
                key={item}
                className={`domestic-form-delivery-item | flex-col-center text-center fw-500 px-1 py-8 pointer fs-link ${
                  item === activeDelivery ? "active" : ""
                }`}
                onClick={() => setActiveDelivery(item)}
              >
                <span style={{ width: "18px" }}>
                  {/* <img loading="lazy" src={first[index]} alt="img" className="custom-img" /> */}
                  {first[index]}
                </span>
                <span className="user-select-none">{item}</span>
              </li>
            ))}
        </ul>
        <div className="| two-column gap-8 t-one-column">
          {/* Source City */}
          <Select
            isSearchable={true}
            options={cityOptions}
            value={b2bSourceCity.value && b2bSourceCity}
            onChange={(e: any) => setB2bSourceCity(e)}
            placeholder={"Source City"}
          />
          {/* Destination City */}
          <Select
            isSearchable={true}
            value={b2bDestinationCity.value && b2bDestinationCity}
            onChange={(e: any) => setB2bDestinationCity(e)}
            options={cityOptions}
            placeholder={"Destination City"}
          />
        </div>
        <div className="| two-column gap-8 t-one-column">
          {/* Source Branch */}
          <Select
            isSearchable={true}
            value={sourceBranch.value && sourceBranch}
            options={filteredSourceBranchListOptions}
            onChange={(e: any) => setSourceBranch(e)}
            placeholder={"Source Branch"}
          />
          {/* Destination Branch */}
          <Select
            isSearchable={true}
            value={destinationBranch.value && destinationBranch}
            options={filteredDestinationBranchListOptions}
            onChange={(e: any) => setDestinationBranch(e)}
            placeholder={"Destination Branch"}
          />
        </div>
        <Button
          variant="secondary"
          action="secondary"
          Icon={<GetDetailsIcon />}
          className="| as-start fs-button my-1"
        >
          Get Estimated Rate and Time
        </Button>
      </div>
    );
  }
  // Door to Door Delivery
  else {
    return (
      <div className="| flex-col gap-1">
        <ul className="| two-column t-one-column m-0">
          {deliveryType.length > 0 &&
            deliveryType.map((item, index) => (
              <li
                key={item}
                className={`domestic-form-delivery-item flex-col-center text-center fw-500 px-1 py-8 pointer fs-link ${
                  item === activeDelivery ? "active" : ""
                }`}
                onClick={() => setActiveDelivery(item)}
              >
                <span style={{ width: "18px" }}>
                  {/* <img loading="lazy" src={second[index]} alt="img" className="custom-img" /> */}
                  {second[index]}
                </span>
                <span className="user-select-none">{item}</span>
              </li>
            ))}
        </ul>
        <div className="| two-column gap-8 t-one-column">
          {/* Source Pincode */}
          <Input
            title="Source Pincode"
            variant="white"
            weight={400}
            width="1px"
            state={{ value: sourcePincode, setValue: setSourcePincode }}
            onBlur={() => validatePincode(sourcePincode, setSourcePincode)}
            warningWeight={400}
            required
            type="tel"
            maxLength={6}
          />
          {/* Destination Pincode */}
          <Input
            title="Destination Pincode"
            variant="white"
            weight={400}
            width="1px"
            state={{
              value: destinationPincode,
              setValue: setDestinationPincode,
            }}
            onBlur={() =>
              validatePincode(destinationPincode, setDestinationPincode)
            }
            warningWeight={400}
            required
            type="tel"
            maxLength={6}
          />
        </div>
        <div className="| two-column gap-8 t-one-column">
          {/* Source city */}
          <Select
            isSearchable={true}
            value={sourceCity.value && sourceCity}
            options={cityOptions}
            placeholder={"Source City"}
            onChange={(e: any) => setSourceCity(e)}
          />
          {/* Destination city */}
          <Select
            isSearchable={true}
            value={destinationCity.value && destinationCity}
            options={cityOptions}
            placeholder={"Destination City"}
            onChange={(e: any) => setDestinationCity(e)}
          />
        </div>
        <Button
          variant="secondary"
          action="secondary"
          Icon={<GetDetailsIcon />}
          className="| as-start fs-button my-1"
        >
          Get Estimated Rate and Time
        </Button>
      </div>
    );
  }
}
